/* eslint-disable prettier/prettier */
import { useState } from 'react';
import Papa, { ParseResult } from 'papaparse';
import * as XLSX from 'xlsx';
import { Minus } from 'lucide-react';
import { toast } from 'react-hot-toast';

import { useAuth } from '../../context/AuthContext';
import { ExcelUploadZone } from '../../components/ui/ExcelSheetUpload';
import { ColumnMappingModal } from '../../components/ui/ColumnMappingModal';
import { CompanyService } from '../../services/company.service';

const REQUIRED_COLUMNS = ['ID', 'Name', 'Country', 'Location'];
const COLUMN_ALIASES: Record<string, string[]> = {
  ID: ['id', 'identifier', 'unique id', 'client id', 'record id', 'company id'],
  Name: ['name', 'full name', 'company name', 'entity name', 'legal name', 'cedant', 'broker'],
  Country: ['country', 'country name', 'nation', 'residence country'],
  Location: ['location', 'city', 'address', 'headquarters', 'region'],
};

type UploadType = 'cedants' | 'brokers';

export const SheetsPage = () => {
  const { user, setUser } = useAuth();

  const [cedantsState, setCedantsState] = useState<{
    rawFile: File | null;
    normalizedFile: File | null;
  }>({ rawFile: null, normalizedFile: null });
  const [brokersState, setBrokersState] = useState<{
    rawFile: File | null;
    normalizedFile: File | null;
  }>({ rawFile: null, normalizedFile: null });

  const [cedantsPreview, setCedantsPreview] = useState<any[][] | null>(null);
  const [brokersPreview, setBrokersPreview] = useState<any[][] | null>(null);

  const [cedantsStatus, setCedantsStatus] = useState<'idle' | 'uploading' | 'error' | 'success'>(
    'idle',
  );
  const [brokersStatus, setBrokersStatus] = useState<'idle' | 'uploading' | 'error' | 'success'>(
    'idle',
  );

  const [showModal, setShowModal] = useState(false);
  const [columnHeaders, setColumnHeaders] = useState<string[]>([]);
  const [pendingMappingData, setPendingMappingData] = useState<{
    type: UploadType;
    data: any[][];
    autoMapping: Record<string, string | null>;
  } | null>(null);

  const sanitizeCsv = (text: string) =>
    text
      .split('\n')
      .map((line) => line.replace(/"{2,}/g, '"'))
      .join('\n');

  const decodeBufferSmart = (buffer: ArrayBuffer): string => {
    try {
      return new TextDecoder('utf-8').decode(buffer);
    } catch {
      return new TextDecoder('iso-8859-1').decode(buffer);
    }
  };

  const fixEncoding = (str: string) => {
    try {
      return decodeURIComponent(escape(str));
    } catch {
      return str;
    }
  };

  const fetchAndPreview = async (type: UploadType, setPreview: (data: any[][]) => void) => {
    try {
      const response = await CompanyService.fetchExcelFile(type);
      const arrayBuffer = response;
      const rawText = decodeBufferSmart(arrayBuffer);
      const sanitized = sanitizeCsv(rawText);

      let parsedData: any[][] = [];

      if (sanitized.includes(',') || sanitized.includes(';') || sanitized.includes('\n')) {
        const parsed: ParseResult<any> = Papa.parse(sanitized, {
          skipEmptyLines: true,
          delimiter: ',',
          quoteChar: '"',
          transform: (value: string) => fixEncoding(value.trim() === '"' ? '' : value.trim()),
        });

        parsedData = parsed.data.map((row: unknown) => {
          if (
            Array.isArray(row) &&
            row.length === 1 &&
            typeof row[0] === 'string' &&
            row[0].includes(',')
          ) {
            return row[0].split(',').map((cell) => fixEncoding(cell.trim()));
          }
          return row as any[];
        });
      } else {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        parsedData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: false,
          defval: '',
        }) as any[][];
      }

      setPreview(parsedData);
    } catch (err) {
      toast.error('Could not fetch file');
      console.error(err);
    }
  };

  const handleFileSelect = async (file: File, type: UploadType) => {
    const arrayBuffer = await file.arrayBuffer();
    const rawText = decodeBufferSmart(arrayBuffer);
    const sanitized = sanitizeCsv(rawText);

    let parsedData: any[][] = [];

    if (sanitized.includes(',') || sanitized.includes(';')) {
      const parsed: ParseResult<any> = Papa.parse(sanitized, {
        skipEmptyLines: true,
        delimiter: ',',
        quoteChar: '"',
        transform: (value: string) => fixEncoding(value.trim() === '"' ? '' : value.trim()),
      });
      parsedData = parsed.data.map((row: unknown) => {
        if (
          Array.isArray(row) &&
          row.length === 1 &&
          typeof row[0] === 'string' &&
          row[0].includes(',')
        ) {
          return row[0].split(',').map((cell) => fixEncoding(cell.trim()));
        }
        return row as any[];
      });
    } else {
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      parsedData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        blankrows: false,
        defval: '',
      }) as any[][];
    }

    const headers = parsedData[0];
    const header = headers.map((cell: string) => cell.toLowerCase().trim());

    const autoMapping: Record<string, string | null> = {};
    let nameFound = false;

    for (const col of REQUIRED_COLUMNS) {
      const index = header.findIndex((h) => COLUMN_ALIASES[col].some((alias) => h.includes(alias)));
      autoMapping[col] = index >= 0 ? headers[index] : null;
      if (col === 'Name' && autoMapping[col]) nameFound = true;
    }

    const hasMissing = Object.values(autoMapping).some((v) => !v);

    if (hasMissing || !nameFound) {
      setColumnHeaders(headers);
      setPendingMappingData({ type, data: parsedData, autoMapping });
      setShowModal(true);

      if (type === 'cedants') setCedantsState({ rawFile: file, normalizedFile: null });
      else setBrokersState({ rawFile: file, normalizedFile: null });

      return;
    }

    handleNormalization(parsedData, autoMapping, file, type);
  };

  const handleNormalization = (
    data: any[][],
    mapping: Record<string, string | null>,
    file: File,
    type: UploadType,
  ) => {
    const headers = data[0];
    const headerMap: Record<string, number | null> = {};
    REQUIRED_COLUMNS.forEach((key) => {
      headerMap[key] = mapping[key] ? headers.indexOf(mapping[key]!) : null;
    });

    const normalized: any[][] = [REQUIRED_COLUMNS];
    for (let i = 1; i < data.length; i++) {
      const row = REQUIRED_COLUMNS.map((key) => {
        if (headerMap[key] !== null) return data[i][headerMap[key] as number] || '';
        if (key === 'ID') return `${i}`;
        return '';
      });
      normalized.push(row);
    }

    const csv = Papa.unparse(normalized);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const normalizedFile = new File([blob], `normalized-${file.name}`, { type: 'text/csv' });

    if (type === 'cedants') {
      setCedantsPreview(normalized);
      setCedantsState({ rawFile: file, normalizedFile });
    } else {
      setBrokersPreview(normalized);
      setBrokersState({ rawFile: file, normalizedFile });
    }

    setShowModal(false);
    setPendingMappingData(null);
  };

  const handleManualMapping = (mapping: Record<string, string | null>) => {
    if (!pendingMappingData) return;
    const { data, type } = pendingMappingData;
    const file = type === 'cedants' ? cedantsState.rawFile! : brokersState.rawFile!;
    handleNormalization(data, mapping, file, type);
  };

  const handleUpload = async (type: UploadType) => {
    const file = type === 'cedants' ? cedantsState.normalizedFile : brokersState.normalizedFile;
    const setStatus = type === 'cedants' ? setCedantsStatus : setBrokersStatus;
    if (!file) return;

    setStatus('uploading');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await CompanyService.uploadExcel(type, formData);

      toast.success(`${type} uploaded`);
      setUser((prev) => {
        if (!prev || !prev.company) return prev;
        const updated = { ...prev, company: { ...prev.company } };
        if (type === 'cedants') updated.company.cedantsFileUrl = res.data.url;
        else updated.company.brokersFileUrl = res.data.url;
        return updated;
      });

      if (type === 'cedants') {
        setCedantsState({ rawFile: null, normalizedFile: null });
        setCedantsPreview(null);
        setCedantsStatus('success');
      } else {
        setBrokersState({ rawFile: null, normalizedFile: null });
        setBrokersPreview(null);
        setBrokersStatus('success');
      }
    } catch (err) {
      console.error('[Upload Error]', err);
      toast.error('Upload failed.');
      setStatus('error');
    }
  };

  const handleDelete = async (type: UploadType) => {
    try {
      await CompanyService.deleteExcel(type);
      toast.success(`${type} deleted`);

      setUser((prev) => {
        if (!prev || !prev.company) return prev;
        const updated = { ...prev, company: { ...prev.company } };
        if (type === 'cedants') updated.company.cedantsFileUrl = null;
        else updated.company.brokersFileUrl = null;
        return updated;
      });
    } catch (err) {
      toast.error('Delete failed');
    }

    if (type === 'cedants') {
      setCedantsState({ rawFile: null, normalizedFile: null });
      setCedantsPreview(null);
      setCedantsStatus('idle');
    } else {
      setBrokersState({ rawFile: null, normalizedFile: null });
      setBrokersPreview(null);
      setBrokersStatus('idle');
    }
  };

  const handleModalClose = () => {
    if (!pendingMappingData) return;
    const { type } = pendingMappingData;
    if (type === 'cedants') setCedantsState({ rawFile: null, normalizedFile: null });
    else setBrokersState({ rawFile: null, normalizedFile: null });
    setShowModal(false);
    setPendingMappingData(null);
  };

  const removeEmptyColumns = (data: any[][]): any[][] => {
    if (!data.length) return [];

    const columnCount = data[0].length;
    const nonEmptyIndices: number[] = [];

    for (let col = 0; col < columnCount; col++) {
      const hasContent = data.slice(1).some((row) => row[col]?.toString().trim() !== '');
      if (hasContent) nonEmptyIndices.push(col);
    }

    return data.map((row) => nonEmptyIndices.map((i) => row[i]));
  };

  const renderPreviewTable = (data: any[][], clear: () => void) => {
    const filtered = removeEmptyColumns(data);

    return (
      <div className="relative mt-2">
        <div className="flex justify-end mb-1">
          <button onClick={clear} className="text-red-500 hover:text-red-600">
            <Minus className="w-5 h-5 text-primary" />
          </button>
        </div>
        <div className="overflow-auto border rounded-lg max-h-96">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-gray-100 text-primary">
              <tr>
                {filtered[0]?.map((cell, idx) => (
                  <th key={idx} className="px-3 py-2 border">
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.slice(1).map((row, rowIndex) => (
                <tr key={rowIndex} className="odd:bg-white even:bg-gray-50">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 border">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-2xl">
      <h2 className="mb-2 text-lg font-semibold text-primary">Cedants</h2>
      <ExcelUploadZone
        fileName={
          cedantsState.rawFile?.name ?? user?.company?.cedantsFileUrl?.split('/').pop() ?? null
        }
        uploadStatus={cedantsStatus}
        onFileSelect={(file) => handleFileSelect(file, 'cedants')}
        onRemoveFile={() => handleDelete('cedants')}
        onUploadClick={
          cedantsState.normalizedFile && !user?.company?.cedantsFileUrl
            ? () => handleUpload('cedants')
            : undefined
        }
        onClickFile={() => {
          if (user?.company?.cedantsFileUrl && !cedantsPreview) {
            fetchAndPreview('cedants', setCedantsPreview);
          }
        }}
      />
      {cedantsPreview && renderPreviewTable(cedantsPreview, () => setCedantsPreview(null))}

      <h2 className="mt-6 mb-2 text-lg font-semibold text-primary">Brokers</h2>
      <ExcelUploadZone
        fileName={
          brokersState.rawFile?.name ?? user?.company?.brokersFileUrl?.split('/').pop() ?? null
        }
        uploadStatus={brokersStatus}
        onFileSelect={(file) => handleFileSelect(file, 'brokers')}
        onRemoveFile={() => handleDelete('brokers')}
        onUploadClick={
          brokersState.normalizedFile && !user?.company?.brokersFileUrl
            ? () => handleUpload('brokers')
            : undefined
        }
        onClickFile={() => {
          if (user?.company?.brokersFileUrl && !brokersPreview) {
            fetchAndPreview('brokers', setBrokersPreview);
          }
        }}
      />
      {brokersPreview && renderPreviewTable(brokersPreview, () => setBrokersPreview(null))}

      <ColumnMappingModal
        isOpen={showModal}
        onClose={handleModalClose}
        headers={columnHeaders}
        requiredColumns={REQUIRED_COLUMNS}
        autoMapping={pendingMappingData?.autoMapping || {}}
        onSubmit={handleManualMapping}
      />
    </div>
  );
};
